import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/store/initializeStore";
import { SUPPORT_REQUESTS_QUERY_KEY, type SupportRequestWithRelations } from "../supportConstants";
import { pendingStatusOverrides } from "./useSupportApi";

export const useTraineeSupportRequests = (traineeId?: string | null) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SUPPORT_REQUESTS_QUERY_KEY, "trainee", traineeId],
    enabled: Boolean(traineeId),
    // Same guard as useSupportRequests: a fetch resolving while a status mutation is
    // in flight (drawer select on the profile tab) must not snap the status back.
    select: (requests: SupportRequestWithRelations[]) =>
      pendingStatusOverrides.size === 0
        ? requests
        : requests.map((request) => {
            const pendingStatus = pendingStatusOverrides.get(request.id);
            return pendingStatus && pendingStatus !== request.status ? { ...request, status: pendingStatus } : request;
          }),
    queryFn: async () => {
      if (!traineeId) {
        return [];
      }

      const { data, error } = await supabase
        .from("support_request")
        .select(
          "*, reporter:reporterUserId(id, email, firstName, lastName), assignee:assigneeUserId(id, email, firstName, lastName, avatarUrl), trainee:traineeId(id, firstName, lastName, email)",
        )
        .eq("traineeId", traineeId)
        .order("createdAt", { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []) as unknown as SupportRequestWithRelations[];
    },
  });

  return { requests: data || [], isLoading, error, refetch };
};
